import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, ExternalLink, Loader2, Share2 } from "lucide-react";
import { ActressCard } from "@/components/ActressCard.tsx";
import { SpecTable } from "@/components/SpecTable.tsx";
import { RadarChart } from "@/components/RadarChart.tsx";
import { fetchActressById, fetchSimilarActresses, fetchActressWorks } from "@/lib/api.ts";
import type { Actress, DmmWork } from "@/lib/types.ts";

export function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const [actress, setActress] = useState<Actress | null>(null);
  const [similar, setSimilar] = useState<Actress[]>([]);
  const [works, setWorks] = useState<DmmWork[]>([]);
  const [worksTotal, setWorksTotal] = useState(0);
  const [worksPage, setWorksPage] = useState(1);
  const [worksLoading, setWorksLoading] = useState(false);
  const [worksLoaded, setWorksLoaded] = useState(false);

  useEffect(() => {
    if (!id) return;
    setActress(null);
    setSimilar([]);
    setWorks([]);
    setWorksPage(1);
    setWorksLoaded(false);
    fetchActressById(id).then((a) => {
      setActress(a);
      if (a) fetchSimilarActresses(a).then(setSimilar);
    });
  }, [id]);

  // Lazy load works when actress is loaded
  useEffect(() => {
    if (!id || worksLoaded) return;
    setWorksLoading(true);
    fetchActressWorks(id, 1, 20).then((result) => {
      setWorks(result.data);
      setWorksTotal(result.total);
      setWorksLoaded(true);
      setWorksLoading(false);
    }).catch(() => setWorksLoading(false));
  }, [id, worksLoaded]);

  const loadMoreWorks = async () => {
    if (!id || worksLoading) return;
    const nextPage = worksPage + 1;
    setWorksLoading(true);
    const result = await fetchActressWorks(id, nextPage, 20);
    setWorks((prev) => [...prev, ...result.data]);
    setWorksPage(nextPage);
    setWorksLoading(false);
  };

  if (!actress) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <p className="text-muted-foreground font-[var(--font-secondary)]">読み込み中...</p>
      </div>
    );
  }

  const hasBwh = actress.bust > 0 || actress.waist > 0 || actress.hip > 0;

  const tweetText = (() => {
    // Twitter: 重み付き280文字制限 (日本語=2, ASCII=1, URL=23固定)
    const TEXT_BUDGET = 256; // 280 - 23(t.co URL) - 1(スペース)

    function tw(s: string): number {
      let w = 0;
      for (const ch of s) w += (ch.codePointAt(0) || 0) <= 0x10FF ? 1 : 2;
      return w;
    }

    function truncate(s: string, maxW: number): string {
      let w = 0, idx = 0;
      for (const ch of s) {
        const cw = (ch.codePointAt(0) || 0) <= 0x10FF ? 1 : 2;
        if (w + cw + 2 > maxW) break; // +2 for "…"
        w += cw;
        idx += ch.length;
      }
      return idx < s.length ? s.slice(0, idx) + "…" : s;
    }

    const specParts: string[] = [];
    if (actress.debutAge != null && actress.latestActiveAge != null) {
      specParts.push(actress.debutAge === actress.latestActiveAge
        ? `出演${actress.debutAge}歳`
        : `出演${actress.debutAge}〜${actress.latestActiveAge}歳`);
    } else if (actress.age > 0) {
      specParts.push(`${actress.age}歳`);
    }
    if (actress.cupSize) specParts.push(`${actress.cupSize}カップ`);
    if (hasBwh) specParts.push(`B${actress.bust}/W${actress.waist}/H${actress.hip}`);
    const header = `${actress.name} (${specParts.join(" ")})`;

    let remaining = TEXT_BUDGET - tw(header);
    const lines: string[] = [header];

    for (const work of works.slice(0, 2)) {
      const prefixW = 1 + 2 + 1; // \n(1) + 📀(2) + space(1)
      if (remaining < prefixW + 8) break;
      const title = truncate(work.title, remaining - prefixW);
      lines.push(`📀 ${title}`);
      remaining -= prefixW + tw(title);
    }

    return lines.join("\n");
  })();

  return (
    <div className="flex flex-col flex-1">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 px-8 lg:px-16 py-5">
        <Link to="/" className="font-[var(--font-secondary)] text-[13px] text-muted-foreground no-underline hover:text-foreground">
          トップ
        </Link>
        <ChevronRight size={14} className="text-muted-foreground" />
        <span className="font-[var(--font-secondary)] text-[13px] font-medium text-foreground">
          {actress.name}
        </span>
      </div>

      {/* Profile Main */}
      <div className="px-8 lg:px-16 py-8 flex flex-col lg:flex-row gap-10">
        {/* Photo Column */}
        <div className="w-full lg:w-[480px] shrink-0">
          <div className="w-full aspect-[4/5] rounded-2xl overflow-hidden">
            {actress.imageUrl ? (
              <img
                src={actress.imageUrl}
                alt={actress.name}
                className="w-full h-full object-cover"
                style={{ viewTransitionName: `actress-img-${actress.id}` }}
              />
            ) : (
              <div className="w-full h-full bg-secondary flex items-center justify-center">
                <span className="text-6xl font-bold text-muted-foreground/40">{actress.name.charAt(0)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Info Column */}
        <div className="flex-1 flex flex-col gap-8">
          {/* Name Section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <h1
                className="font-[var(--font-secondary)] text-4xl font-bold text-foreground"
                style={{ viewTransitionName: `actress-name-${actress.id}` }}
              >
                {actress.name}
              </h1>
              <a
                href={`https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(`https://dekapai.dev/actress/${actress.id}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground hover:bg-muted transition-colors no-underline shrink-0"
              >
                <Share2 size={14} />
                <span className="font-[var(--font-secondary)] text-xs font-medium">Post</span>
              </a>
            </div>
            <div className="flex items-center gap-3">
              {actress.age > 0 && (
                <span className="font-[var(--font-secondary)] text-sm text-muted-foreground">
                  {actress.age}歳
                </span>
              )}
              {actress.debutAge != null && actress.latestActiveAge != null && (
                <span className="font-[var(--font-secondary)] text-sm text-muted-foreground">
                  出演{actress.debutAge === actress.latestActiveAge
                    ? `${actress.debutAge}歳`
                    : `${actress.debutAge}〜${actress.latestActiveAge}歳`}
                </span>
              )}
              {actress.height > 0 && (
                <span className="font-[var(--font-secondary)] text-sm text-muted-foreground">
                  身長 {actress.height}cm
                </span>
              )}
            </div>
            <div className="flex gap-2 mt-1">
              {actress.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground font-[var(--font-secondary)] text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Spec Table */}
          <SpecTable actress={actress} />

          {/* Radar Chart */}
          {hasBwh && (
            <div className="flex flex-col gap-4">
              <h2 className="font-[var(--font-secondary)] text-base font-semibold text-foreground">
                ボディバランスチャート
              </h2>
              <div className="rounded-xl border border-border bg-card p-6">
                <RadarChart
                  bust={actress.bust}
                  waist={actress.waist}
                  hip={actress.hip}
                  height={actress.height}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Works Section */}
      <div className="px-8 lg:px-16 py-8 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h2 className="font-[var(--font-secondary)] text-2xl font-bold text-foreground">
            出演作品
          </h2>
          <p className="font-[var(--font-secondary)] text-sm text-muted-foreground">
            {worksLoaded && worksTotal > 0
              ? `${actress.name}の出演作品 全${worksTotal}件`
              : worksLoading
                ? "作品情報を読み込み中..."
                : ""}
          </p>
        </div>

        {worksLoading && works.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {works.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {works.map((work) => {
              const isOnSale = work.campaign != null && work.campaign.end > new Date().toISOString().replace("T", " ");
              const hasDiscount = isOnSale && work.listPrice != null && work.minPrice != null && work.listPrice > work.minPrice;
              return (
                <a
                  key={work.contentId}
                  href={work.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-lg overflow-hidden bg-card border border-border shadow-sm hover:shadow-md transition-shadow no-underline relative"
                >
                  <div className="w-full aspect-[2/3] overflow-hidden bg-secondary relative">
                    {work.imageUrl ? (
                      <img
                        src={work.imageUrl}
                        alt={work.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground/30 text-sm">
                        No Image
                      </div>
                    )}
                    {hasDiscount && (
                      <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-red-500 text-white text-[10px] font-bold">
                        SALE
                      </span>
                    )}
                  </div>
                  <div className="p-3 flex flex-col gap-1.5">
                    <h4 className="font-[var(--font-secondary)] text-xs font-medium text-foreground line-clamp-2 leading-relaxed">
                      {work.title}
                    </h4>
                    {hasDiscount ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-[var(--font-secondary)] text-xs font-bold text-red-500">
                          ¥{work.minPrice!.toLocaleString()}
                        </span>
                        <span className="font-[var(--font-secondary)] text-[10px] text-muted-foreground line-through">
                          ¥{work.listPrice!.toLocaleString()}
                        </span>
                      </div>
                    ) : work.minPrice != null ? (
                      <span className="font-[var(--font-secondary)] text-xs text-muted-foreground">
                        ¥{work.minPrice.toLocaleString()}~
                      </span>
                    ) : null}
                    <div className="flex items-center justify-between">
                      <span className="font-[var(--font-secondary)] text-[11px] text-muted-foreground">
                        {work.date}
                      </span>
                      <ExternalLink size={12} className="text-muted-foreground" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}

        {worksLoaded && works.length === 0 && (
          <div className="text-center py-12 text-muted-foreground font-[var(--font-secondary)] text-sm">
            出演作品が見つかりませんでした
          </div>
        )}

        {worksLoaded && works.length < worksTotal && (
          <button
            onClick={loadMoreWorks}
            disabled={worksLoading}
            className="mx-auto px-8 py-2.5 rounded-full bg-secondary text-secondary-foreground font-[var(--font-secondary)] text-sm font-medium border-0 cursor-pointer hover:bg-muted transition-colors disabled:opacity-50"
          >
            {worksLoading ? "読み込み中..." : "もっと見る"}
          </button>
        )}
      </div>

      {/* Related Section */}
      <div className="px-8 lg:px-16 py-8 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h2 className="font-[var(--font-secondary)] text-2xl font-bold text-foreground">
            類似スペックの女優
          </h2>
          <p className="font-[var(--font-secondary)] text-sm text-muted-foreground">
            {actress.name}と似たスペックの女優をご紹介します
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {similar.map((a) => (
            <ActressCard key={a.id} actress={a} />
          ))}
        </div>
      </div>
    </div>
  );
}
